<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('form.add'), type: 'confirm', disabled: isDisabled, callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full py-4 px-4 flex flex-col"
  >
    <div v-if="serviceForm" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ $t('form.add') }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300">
          {{ $t('components.service.actions.addForm.addInfo') }}
        </p>
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <o-scroll class="py-4 px-8 h-full">
          <o-form ref="form" class="h-full">
            <div class="flex space-x-4 mb-6">
              <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.form')" :info="$t('components.service.actions.addForm.formInfo')">
                <form-select v-model="serviceForm.form" :status="status" class="w-full shadow-sm" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('general.frequency')" :info="$t('form.frequencyInfo')">
                <select-field v-model="serviceForm.frequency" :options="serviceFormFrequencyOptions" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('service.requestMode')" :info="$t('service.requestModeInfo')">
                <select-field v-model="serviceForm.requirementMode" :options="serviceFormRequirementModeOptions" />
              </o-field-group>
            </div>

            <o-field-group
              v-if="isRequirementModeScheduled"
              v-slot="{ status }"
              :label="$t('form.scheduledTime')"
              :name="$t('form.scheduledTime')"
              :info="$t('form.scheduledTimeInfo')"
              class="mb-4 block"
              rules="required"
            >
              <div class="flex items-center space-x-2">
                <number-field
                  v-model="serviceForm.scheduleOffsetValue"
                  type="number"
                  :status="status"
                  class="w-1/2"
                />
                <segment-field v-model="serviceForm.scheduleOffsetMode" :options="scheduleOffsetOptions" class="border w-1/2" />
              </div>
            </o-field-group>
          </o-form>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import FormSelect from '@/components/form/elements/Select.vue'
import { OAlert, OFieldGroup, OForm } from '@/components/elements'
import { TextField, SelectField, NumberField, SegmentField } from '@/components/fields'

import {
  ServiceForm,
  ServiceFormFrequency,
  ServiceFormFrequencyLabels,
  ServiceFormRequirementMode,
  ServiceFormRequirementModeLabels,
} from '~/_api/models/serviceForm'
import type { Service } from '~/_api/models/service'

const props = defineProps<{
  service: Service
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
  (e: 'update:loading', loading: boolean): void
}>()

const { $api, $notification, $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

// Template refs
const modal = ref()
const form = ref()

// State
const serviceForm = ref<ServiceForm | null>(null)

const serviceFormFrequencyOptions = computed(() => {
  return Object.values(ServiceFormFrequency)
    .filter((value): value is ServiceFormFrequency => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceFormFrequencyLabels[value]),
      value,
    }))
})

const serviceFormRequirementModeOptions = computed(() => {
  return Object.values(ServiceFormRequirementMode)
    .filter((value): value is ServiceFormRequirementMode => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceFormRequirementModeLabels[value]),
      value,
    }))
})

const scheduleOffsetOptions = computed(() => [
  { label: $t('form.before'), value: 'before' },
  { label: $t('form.after'), value: 'after' },
])

const isDisabled = computed(() => !serviceForm.value?.form)

const isRequirementModeScheduled = computed(() => {
  return serviceForm.value?.isRequirementModeScheduled
})

const setDefaults = () => {
  const data = {
    form: null,
    frequency: ServiceFormFrequency.reservation,
    requirementMode: ServiceFormRequirementMode.confirmation,
    scheduleOffset: -3,
  }

  serviceForm.value = new ServiceForm(data)
}

const open = () => {
  setDefaults()
  modal.value?.open()
}

const validate = () => {
  return form.value?.validate()
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.addForm(props.service, serviceForm.value!)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $t('components.service.actions.addForm.success'),
        text: $t('components.service.actions.addForm.successInfo', { title: serviceForm.value?.form?.title }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)
      emit('update:loading', false)

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $t('components.service.actions.addForm.error'),
      text: $t('components.service.actions.addForm.errorInfo', { title: serviceForm.value?.form?.title }),
    })

    emit('update:loading', false)
    emit('error')
  }
}

defineExpose({
  open,
})
</script>
